import { Fragment, useMemo } from "react";
import type { Distributor, InventoryItem, Product } from "@/types";
import { categoryBadge } from "@/utils/categories";
import { formatCOP, formatNumber } from "@/utils/format";
import AdvisorConsignmentBreakdown from "@/pages/admin/inventario/components/AdvisorConsignmentBreakdown";

// Stock del admin (Oficina) y total en consignación (manos de la red).
const oficinaOf = (item?: InventoryItem): number => item?.stockCasa ?? 0;
const consignedOf = (item?: InventoryItem): number =>
  item ? Object.values(item.stockByAdvisor).reduce((s, n) => s + n, 0) : 0;
const totalOf = (item?: InventoryItem): number => oficinaOf(item) + consignedOf(item);

interface InventoryMatrixProps {
  products: Product[];
  inventory: InventoryItem[];
  distributors: Distributor[];
  pendingProductIds?: string[];
  onEdit: (product: Product) => void;
}

// Matriz consolidada: stock en Oficina (admin) + total en consignación por producto.
const InventoryMatrix = ({
  products,
  inventory,
  distributors,
  pendingProductIds = [],
  onEdit,
}: InventoryMatrixProps) => {
  const pendingSet = useMemo(() => new Set(pendingProductIds), [pendingProductIds]);
  const groups = useMemo(() => {
    const map = new Map<string, Product[]>();
    products.forEach((p) => {
      const arr = map.get(p.category) ?? [];
      arr.push(p);
      map.set(p.category, arr);
    });
    return Array.from(map.entries());
  }, [products]);

  const invOf = (productId: string) => inventory.find((i) => i.productId === productId);

  const grandUnits = products.reduce((acc, p) => acc + totalOf(invOf(p.id)), 0);
  const grandConsigned = products.reduce((acc, p) => acc + consignedOf(invOf(p.id)), 0);
  const grandValue = products.reduce(
    (acc, p) => acc + totalOf(invOf(p.id)) * p.pricePublic,
    0
  );

  if (products.length === 0) {
    return (
      <div className="glass-panel rounded-lg p-10 text-center">
        <span className="mx-auto w-14 h-14 rounded-lg bg-accent-500/15 text-accent-200 flex items-center justify-center mb-4">
          <i className="ri-archive-2-line text-2xl" />
        </span>
        <h3 className="editable-title text-base font-bold">Sin inventario para mostrar</h3>
        <p className="text-xs text-foreground-600 mt-1">
          Ajusta la búsqueda o carga productos al catálogo para empezar a controlar el stock.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="glass-panel rounded-lg overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[820px] text-left">
            <thead>
              <tr className="border-b border-accent-500/20 text-[11px] uppercase tracking-wide text-foreground-500">
                <th className="px-4 py-3 font-semibold">Producto</th>
                <th className="px-3 py-3 font-semibold text-right">Oficina</th>
                <th className="px-3 py-3 font-semibold text-right whitespace-nowrap">Consignación</th>
                <th className="px-3 py-3 font-semibold text-right">Total</th>
                <th className="px-4 py-3 font-semibold text-right">Valor</th>
                <th className="px-3 py-3 font-semibold text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {groups.map(([cat, list]) => {
                const groupUnits = list.reduce((acc, p) => acc + totalOf(invOf(p.id)), 0);
                const groupValue = list.reduce(
                  (acc, p) => acc + totalOf(invOf(p.id)) * p.pricePublic,
                  0
                );
                return (
                  <Fragment key={`g-${cat}`}>
                    <tr className="bg-background-200/40">
                      <td colSpan={6} className="px-4 py-2.5">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <span className="flex items-center gap-2">
                            <span
                              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${categoryBadge(cat)}`}
                            >
                              {cat}
                            </span>
                            <span className="text-[11px] text-foreground-500">
                              {list.length} referencias
                            </span>
                          </span>
                          <span className="text-[11px] text-foreground-600">
                            {formatNumber(groupUnits)} und · {formatCOP(groupValue)}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {list.map((p) => {
                      const item = invOf(p.id);
                      const total = totalOf(item);
                      const consigned = consignedOf(item);
                      return (
                        <tr
                          key={p.id || p.sku}
                          className={`border-b border-background-300/25 hover:bg-background-200/25 transition-colors ${
                            pendingSet.has(p.id)
                              ? "bg-primary-500/10 border-l-4 border-l-primary-500"
                              : ""
                          }`}
                        >
                          <td className="px-4 py-3">
                            <span className="block font-medium text-foreground-900 max-w-[240px] truncate">
                              {p.name}
                            </span>
                            <span className="block font-mono text-[10px] text-accent-200">{p.sku}</span>
                            {pendingSet.has(p.id) && (
                              <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-primary-200">
                                <i className="ri-alert-line" /> Compra pendiente de pago
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right text-foreground-800 whitespace-nowrap">
                            {formatNumber(oficinaOf(item))}
                          </td>
                          <td className="px-3 py-3 text-right whitespace-nowrap">
                            <span
                              className={
                                consigned > 0 ? "text-secondary-200 font-semibold" : "text-foreground-500"
                              }
                            >
                              {formatNumber(consigned)}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right font-bold text-foreground-950 whitespace-nowrap">
                            {formatNumber(total)}
                          </td>
                          <td className="px-4 py-3 text-right text-foreground-700 whitespace-nowrap">
                            {formatCOP(total * p.pricePublic)}
                          </td>
                          <td className="px-3 py-3 text-right">
                            <button
                              onClick={() => onEdit(p)}
                              className="w-8 h-8 ml-auto flex items-center justify-center rounded-md text-foreground-600 hover:text-accent-200 hover:bg-background-300/40 cursor-pointer"
                              aria-label="Editar stock"
                              title="Editar stock"
                            >
                              <i className="ri-pencil-line" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-accent-500/25 bg-background-200/50">
                <td
                  colSpan={3}
                  className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-foreground-600"
                >
                  Total consolidado · {formatNumber(grandConsigned)} und en consignación
                </td>
                <td className="px-3 py-3 text-right font-extrabold text-foreground-950 whitespace-nowrap">
                  {formatNumber(grandUnits)}
                </td>
                <td className="px-4 py-3 text-right font-extrabold text-accent-200 whitespace-nowrap">
                  {formatCOP(grandValue)}
                </td>
                <td className="px-3 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Vista móvil en tarjetas */}
        <ul className="md:hidden divide-y divide-background-300/25">
          {groups.map(([cat, list]) => (
            <li key={`m-${cat}`} className="px-4 py-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${categoryBadge(cat)}`}
                >
                  {cat}
                </span>
                <span className="text-[11px] text-foreground-500">{list.length} referencias</span>
              </div>
              <div className="space-y-2">
                {list.map((p) => {
                  const item = invOf(p.id);
                  const total = totalOf(item);
                  const consigned = consignedOf(item);
                  return (
                    <div
                      key={p.id || p.sku}
                      className={`rounded-md border bg-background-100/50 p-3 ${
                        pendingSet.has(p.id)
                          ? "border-primary-500/60 bg-primary-500/10"
                          : "border-background-300/40"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-900 break-words">
                            {p.name}
                          </p>
                          <p className="font-mono text-[10px] text-accent-200 mt-0.5">{p.sku}</p>
                          {pendingSet.has(p.id) && (
                            <p className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-primary-200">
                              <i className="ri-alert-line" /> Compra pendiente de pago
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => onEdit(p)}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:text-accent-200 hover:bg-background-300/40 cursor-pointer shrink-0"
                          aria-label="Editar stock"
                          title="Editar stock"
                        >
                          <i className="ri-pencil-line" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2.5 py-1.5">
                          <p className="text-foreground-500">Oficina</p>
                          <p className="font-semibold text-foreground-800">
                            {formatNumber(oficinaOf(item))}
                          </p>
                        </div>
                        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2.5 py-1.5">
                          <p className="text-foreground-500">Consignación</p>
                          <p className="font-semibold text-secondary-200">
                            {formatNumber(consigned)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-background-300/30">
                        <span className="text-[11px] text-foreground-600">
                          Total{" "}
                          <span className="font-bold text-foreground-950">{formatNumber(total)}</span>{" "}
                          und
                        </span>
                        <span className="text-[11px] font-semibold text-accent-200">
                          {formatCOP(total * p.pricePublic)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <AdvisorConsignmentBreakdown
        products={products}
        inventory={inventory}
        distributors={distributors}
      />
    </>
  );
};

export default InventoryMatrix;